import { AccountInfo, AuthenticationResult, BrowserAuthError, PublicClientApplication } from '@azure/msal-browser';
import { PublicClientApplication as PublicClientApplicationHolder } from 'src/utilities/azure/hooks/client';

const accessTokenRequest = {
  redirectUri: `${window.location.origin}/callback/azure`,
  scopes: ['openid'],
};

/**
 * @param {boolean} [force=false] 既に初期化済みの場合であっても実行する
 * @returns 初期化済みの{@link PublicClientApplication}
 */
export const initialize = (() => {
  // Closure States
  let initialized: null | Promise<PublicClientApplication> = null;

  // Implement
  return (force = false) => {
    if (force || initialized === null) {
      const pca = PublicClientApplicationHolder.getInstance();
      initialized = pca.initialize().then(() => pca);
      return initialized;
    } else {
      // 過去の呼び出しで実行中の初期化処理があればその処理の結果を返す
      return initialized;
    }
  };
})();


type IdTokenClaims = {
  exp?: number;  // 有効期限 (秒)
  nbf?: number; // 有効開始時刻 (秒)
  iat?: number; // 発行時刻 (秒)
  iss?: string; // 発行者
  aud?: string; // 受信者 (クライアントID)
  [key: string]: unknown;
}

type ValidationResult = 
  | { valid: true }
  | { valid: false; reason: string };

// IDトークンの検証
const validateIdToken = (
  claims: IdTokenClaims
): ValidationResult => {
  const now = Math.floor(Date.now() / 1000);

    // validation
  if (!claims.exp) {
    return { valid: false, reason: 'exp がありません' };
  }
  if (now >= claims.exp) {
    return { valid: false, reason: 'トークンの有効期限が切れています' };
  }

  if (claims.nbf && now < claims.nbf) {
    return { valid: false, reason: 'トークンはまだ有効化されていません' };
  }

  if (claims.iat && now - claims.iat > 60){
    return { valid: false, reason: 'トークンの発行から1分経過しました。' };
  }

  return { valid: true };
};

// アカウント情報からIDトークンを取得する
export const getAccountIdToken = async (): Promise<string | null> => {
  try{
    // アカウント情報の取得
    const instance = PublicClientApplicationHolder.getInstance();
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0){
      return null;
    }
    const account = accounts[0];
    if (!account){
      return null;
    }

    // クレームの取得
    const claims = account.idTokenClaims;
    if (!claims){
      return null;
    }
    
    // IDトークンの検証
    const result = validateIdToken(claims as IdTokenClaims);

    if (!result.valid){
      // 検証失敗時はnullを返却
      return null;
    }

    // アカウント情報からIDトークンを取得
    const idToken = account.idToken;

    if (!idToken){
      return null;
    }

    return idToken;

  }
  catch(error){
    console.error('Failed to get account.idToken', error);
    return null;
  }
};


export const setActiveAccount = (account: AccountInfo | null) => {
  if (PublicClientApplicationHolder.hasInstance()) {
    PublicClientApplicationHolder.getInstance().setActiveAccount(account);
  }
};

export const acquireTokenSilent = async () => {
  return new Promise<AuthenticationResult | void>((resolve, reject) => {
    if (PublicClientApplicationHolder.hasInstance()) {
      PublicClientApplicationHolder.getInstance()
        .acquireTokenSilent(accessTokenRequest)
        .then((response) => {
          return resolve(response);
        })
        .catch((error) => {
          console.error(error);
          return reject(error);
        });
    } else {
      return resolve();
    }
  });
};

export const acquireTokenRedirect = async () => {
  return new Promise<void>((resolve, reject) => {
    if (PublicClientApplicationHolder.hasInstance()) {
      PublicClientApplicationHolder.getInstance()
        .acquireTokenRedirect(accessTokenRequest)
        .then(() => {
          return resolve();
        })
        .catch((error) => {
          if (error instanceof BrowserAuthError) {
            if (error.errorCode === 'interaction_in_progress') {
              // interaction_in_progressエラーは無視する
              console.log(error);
              return resolve();
            } else {
              return reject(error);
            }
          }
          return reject(error);
        });
    } else {
      return resolve();
    }
  });
};
